import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productionOrderService } from '../services/productionOrderService';

interface SummaryStats {
  totalOrders: number;
  pendingOrders: number;
  inProgressOrders: number;
  completedOrders: number;
  todaysOrders: number;
}

const ProductionOrdersSummaryWidget: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SummaryStats>({
    totalOrders: 0,
    pendingOrders: 0,
    inProgressOrders: 0,
    completedOrders: 0,
    todaysOrders: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSummaryStats();
  }, []);

  const loadSummaryStats = async () => {
    setLoading(true);
    try {
      // Get all production orders
      const orders = await productionOrderService.getAllProductionOrders();

      // Calculate today's date in YYYY-MM-DD format
      const today = new Date();
      const todayYMD = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      // Calculate stats
      const totalOrders = orders.length;
      const pendingOrders = orders.filter((order) => order.status === 'pending').length;
      const inProgressOrders = orders.filter((order) => order.status === 'in_progress').length;
      const completedOrders = orders.filter((order) => order.status === 'completed').length;

      // Count today's orders (scheduled for today)
      const todaysOrders = orders.filter((order) => {
        if (!order.planned_production_date) return false;
        const orderDateStr = order.planned_production_date.split('T')[0];
        return orderDateStr === todayYMD;
      }).length;

      setStats({
        totalOrders,
        pendingOrders,
        inProgressOrders,
        completedOrders,
        todaysOrders,
      });
    } catch (err) {
      console.error('Error loading production orders summary:', err);
      setError('Failed to load production orders summary');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = () => {
    navigate('/production-dashboard');
  };

  if (loading) {
    return (
      <div className="production-orders-summary-widget">
        <h3>Production Orders</h3>
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="production-orders-summary-widget">
        <h3>Production Orders</h3>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="production-orders-summary-widget">
      <h3>Production Orders</h3>
      <div className="summary-grid">
        <div className="summary-card clickable" onClick={handleCardClick}>
          <div className="summary-card-header">
            <span className="summary-icon">📋</span>
            <span className="summary-title">Total Orders</span>
          </div>
          <div className="summary-value">{stats.totalOrders}</div>
          <div className="summary-subtitle">All production orders</div>
        </div>

        <div className="summary-card clickable" onClick={handleCardClick}>
          <div className="summary-card-header">
            <span className="summary-icon">📅</span>
            <span className="summary-title">Today's Orders</span>
          </div>
          <div className="summary-value">{stats.todaysOrders}</div>
          <div className="summary-subtitle">Scheduled for today</div>
        </div>

        <div className="summary-card clickable" onClick={handleCardClick}>
          <div className="summary-card-header">
            <span className="summary-icon">⏳</span>
            <span className="summary-title">Pending</span>
          </div>
          <div className="summary-value">{stats.pendingOrders}</div>
          <div className="summary-subtitle">Awaiting production</div>
        </div>

        <div className="summary-card clickable" onClick={handleCardClick}>
          <div className="summary-card-header">
            <span className="summary-icon">⚙️</span>
            <span className="summary-title">In Progress</span>
          </div>
          <div className="summary-value">{stats.inProgressOrders}</div>
          <div className="summary-subtitle">Currently producing</div>
        </div>
      </div>
    </div>
  );
};

export default ProductionOrdersSummaryWidget;
